/**
 * The token that opens an anonymous order page.
 *
 * The quotation email links `/orders/anonymous/{id}#token=...`. A browser never sends
 * the fragment to a server or in a Referer, so the token stays out of access logs; the
 * page sends it to the API in this header instead of the query string.
 *
 * This module deliberately imports nothing: `app/index.tsx` calls
 * `stashAnonymousOrderTokenFromHash` at the top of bootstrap, before Sentry initializes.
 */
export const ANONYMOUS_ORDER_TOKEN_HEADER = "x-order-token"

const STORAGE_KEY_PREFIX = "anonymous-order-token:"
const ANONYMOUS_ORDER_PATH = /^\/orders\/anonymous\/([^/]+)\/?$/

/** This load's token, for when storage is unavailable. */
let stashed: { orderId: string; token: string } | null = null

/** The token in a location fragment such as `#token=abc`, if it has one. */
export const parseAnonymousOrderToken = (hash: string): string | undefined =>
  new URLSearchParams(hash.replace(/^#/, "")).get("token") || undefined

const keep = (orderId: string, token: string) => {
  stashed = { orderId, token }
  try {
    window.sessionStorage.setItem(STORAGE_KEY_PREFIX + orderId, token)
  } catch {
    // Storage unavailable (private mode, quota): this load still has the token.
  }
}

const dropFragment = () => {
  const { pathname, search } = window.location
  window.history.replaceState(window.history.state, "", pathname + search)
}

/**
 * Moves an order link's token out of the address bar at the very top of bootstrap.
 *
 * Sentry initializes before any route runs and attaches the page URL to an error
 * report, so a failure while the app or the route loads would otherwise send the token
 * with it. The token is kept for the tab, where `takeAnonymousOrderToken` reads it.
 */
export const stashAnonymousOrderTokenFromHash = () => {
  const orderId = ANONYMOUS_ORDER_PATH.exec(window.location.pathname)?.[1]
  const token = parseAnonymousOrderToken(window.location.hash)
  if (orderId && token) {
    keep(orderId, token)
    dropFragment()
  }
}

/**
 * The token for `orderId`.
 *
 * Usually stashed at bootstrap; kept for the tab so a reload still opens the order. A
 * token still in the address bar (the page reached without a full load) is taken and
 * removed the same way. A link emailed before there were tokens has none; the API still
 * opens those for a while.
 */
export const takeAnonymousOrderToken = (
  orderId: string
): string | undefined => {
  const fromLink = parseAnonymousOrderToken(window.location.hash)
  if (fromLink) {
    keep(orderId, fromLink)
    dropFragment()
    return fromLink
  }
  if (stashed?.orderId === orderId) {
    return stashed.token
  }
  try {
    return window.sessionStorage.getItem(STORAGE_KEY_PREFIX + orderId) ?? undefined
  } catch {
    return undefined
  }
}

/** Drops a token the API refused, so a reload does not send it again. */
export const forgetAnonymousOrderToken = (orderId: string) => {
  if (stashed?.orderId === orderId) {
    stashed = null
  }
  try {
    window.sessionStorage.removeItem(STORAGE_KEY_PREFIX + orderId)
  } catch {
    // Nothing kept to forget.
  }
}
